import { useState, useEffect, type ReactNode } from "react";
import "./index.css";

// ─── Config ───────────────────────────────────────────

const APP_URL = "https://app.mpprod.ru/";
const SUPPORT_TG = "https://t.me/mpprod_support";
const SELLER_COUNT = 1000;

// Legal docs live in Google Docs (external, open in a new tab)
const OFFER_URL = "https://docs.google.com/document/d/1MsNLWezQluXAjRIMk-E8hiJqCG2IU1qw7wWxtfXOW1w/edit?usp=sharing";
const POLICY_URL = "https://docs.google.com/document/d/1yH5OUspxcTCcJlHB5RNtbHsEGs29I3v9CGwRwH1EVUs/edit?usp=sharing";

/** 1000 → "1 000+" (narrow no-break space, per the design's fmt()) */
function formatSellers(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "+";
}

const SELLERS = formatSellers(SELLER_COUNT);

// ─── Shared style fragments ───────────────────────────

const GRAD = "linear-gradient(135deg,var(--acc),var(--acc2))";
const btnOutline =
  "inline-block text-center rounded-[14px] border border-white/[0.16] text-[#eef1fb] font-semibold transition-colors hover:border-white/[0.32]";
const legalLink = "text-[var(--acc2)] hover:text-[#eef1fb] transition-colors";

// External legal link (opens in a new tab)
function Legal({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener" className={legalLink}>
      {children}
    </a>
  );
}

// ─── Header ───────────────────────────────────────────

const NAV_LINKS = [
  { label: "Инструменты", href: "/instrumenty/" },
  { label: "Как работает", href: "#how" },
  { label: "Тарифы", href: "#pricing" },
  { label: "Вопросы", href: "#faq" },
  { label: "Контакты", href: "/contacts/" },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-[14px] bg-[rgba(6,10,30,0.72)] border-b border-white/[0.07]">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <a href="#top" className="flex items-center gap-2.5 text-[#eef1fb] font-extrabold text-xl tracking-[-0.02em]">
            <span
              className="inline-flex w-[30px] h-[30px] rounded-[9px] items-center justify-center font-mono text-[15px] text-[#0a0f24] font-bold"
              style={{ background: GRAD }}
            >
              M
            </span>
            MPPROD
          </a>

          <nav className="hidden min-[861px]:flex items-center gap-[26px] text-[15px] font-medium text-[#c3c9e6]">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-[#eef1fb] transition-colors">{l.label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              aria-label="Открыть меню"
              onClick={() => setMenuOpen((v) => !v)}
              className="min-[861px]:hidden flex flex-col items-center justify-center gap-1 w-11 h-11 bg-white/[0.06] border border-white/[0.12] rounded-[11px] cursor-pointer"
            >
              <span className="block w-5 h-0.5 bg-[#eef1fb] rounded-[2px]" />
              <span className="block w-5 h-0.5 bg-[#eef1fb] rounded-[2px]" />
              <span className="block w-5 h-0.5 bg-[#eef1fb] rounded-[2px]" />
            </button>
            <a href={APP_URL} className="hidden min-[861px]:block text-[#c3c9e6] font-semibold text-[15px] px-3.5 py-2.5 hover:text-[#eef1fb] transition-colors">
              Войти
            </a>
            <a href={APP_URL} className="text-[#0a0f24] font-bold text-[15px] px-5 py-2.5 rounded-xl transition-transform hover:-translate-y-0.5" style={{ background: GRAD }}>
              Попробовать бесплатно
            </a>
          </div>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="min-[861px]:hidden relative z-[49] bg-[rgba(6,10,30,0.98)] border-b border-white/[0.08] px-[18px] pt-2 pb-4 flex flex-col">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setMenuOpen(false)}
              className="text-[#eef1fb] font-semibold text-[16px] py-3.5 px-1.5 border-b border-white/[0.06]"
            >
              {l.label}
            </a>
          ))}
          <a
            href={APP_URL}
            onClick={() => setMenuOpen(false)}
            className="text-[var(--acc2)] font-bold text-[16px] py-3.5 px-1.5"
          >
            Войти в кабинет
          </a>
        </div>
      )}
    </>
  );
}

// ─── Hero ─────────────────────────────────────────────

function Hero() {
  return (
    <section className="pt-10 pb-8 min-[861px]:pt-24 min-[861px]:pb-[72px] grid grid-cols-1 min-[861px]:grid-cols-[1.05fr_0.95fr] gap-7 min-[861px]:gap-14 items-center">
      <div style={{ animation: "rise 0.7s ease both" }}>
        <div className="inline-flex items-center gap-2 px-3.5 py-[7px] border border-white/[0.12] rounded-full text-[13px] font-semibold text-[#c3c9e6] mb-6">
          <span className="w-[7px] h-[7px] rounded-full bg-[var(--acc2)] shadow-[0_0_10px_var(--acc2)]" />
          AI-платформа для маркетплейсов
        </div>

        <h1 className="text-[clamp(30px,6.5vw,56px)] leading-[1.05] tracking-[-0.03em] font-extrabold m-0 mb-[22px]">
          Сильные карточки на&nbsp;Wildberries с&nbsp;
          <span className="text-gradient">искусственным интеллектом</span>
        </h1>

        <p className="text-[19px] leading-[1.6] text-[#aab2d6] m-0 mb-[34px] max-w-[520px]">
          Анализ и SEO карточек за 60 секунд, студийные фото и инфографика по одному фото,
          автоответы на отзывы. Всё в одном кабинете.
        </p>

        <div className="flex flex-wrap gap-3.5 items-center">
          <a href={APP_URL} className="text-[#0a0f24] font-bold text-[17px] px-[30px] py-4 rounded-[14px] shadow-[0_12px_40px_rgba(124,92,255,0.35)] transition-transform hover:-translate-y-0.5" style={{ background: GRAD }}>
            Попробовать бесплатно
          </a>
          <a href="#how" className={`${btnOutline} text-[17px] px-[26px] py-4`}>
            Как это работает
          </a>
        </div>

        <div className="flex items-center gap-[22px] mt-[34px] text-[14px] text-[#8b93ba]">
          <span>Более <b className="text-[#eef1fb] font-bold">{SELLERS}</b> селлеров</span>
          <span className="w-px h-4 bg-white/[0.14]" />
          <span>Без пароля от кабинета WB</span>
        </div>
      </div>

      {/* Product mock — illustrative UI, not a data claim */}
      <div style={{ animation: "floaty 6s ease-in-out infinite" }}>
        <div className="border border-white/10 rounded-[22px] p-[22px] shadow-[0_30px_80px_rgba(0,0,0,0.5)] bg-[linear-gradient(180deg,rgba(20,26,54,0.9),rgba(11,15,36,0.9))]">
          <div className="flex items-center justify-between mb-[18px]">
            <div className="font-mono text-[12px] text-[#8b93ba] tracking-[0.04em]">АНАЛИЗ КАРТОЧКИ</div>
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white/20" />
              <span className="w-2 h-2 rounded-full bg-white/20" />
              <span className="w-2 h-2 rounded-full bg-[var(--acc2)]" />
            </div>
          </div>

          <div className="flex gap-4 items-center mb-[18px]">
            <div
              className="w-[88px] h-[88px] rounded-2xl flex items-center justify-center font-mono text-[9px] text-[#6b74a0] text-center leading-tight shrink-0"
              style={{
                background:
                  "repeating-linear-gradient(45deg,rgba(255,255,255,0.06),rgba(255,255,255,0.06) 8px,rgba(255,255,255,0.02) 8px,rgba(255,255,255,0.02) 16px)",
              }}
            >
              фото<br />товара
            </div>
            <div className="flex-1">
              <div className="font-bold text-[15px] mb-2.5">Оценка карточки</div>
              <div className="h-[9px] rounded-md bg-white/[0.08] overflow-hidden">
                <div className="w-[82%] h-full" style={{ background: "linear-gradient(90deg,var(--acc),var(--acc2))" }} />
              </div>
              <div className="flex justify-between mt-[7px] text-[12px] text-[#8b93ba]">
                <span>SEO · заголовок · фото</span>
                <span className="text-[var(--acc2)] font-bold">82 / 100</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-[12px] px-[11px] py-1.5 rounded-lg bg-[rgba(124,92,255,0.16)] border border-[rgba(124,92,255,0.3)]">+ ключевой запрос</span>
            <span className="text-[12px] px-[11px] py-1.5 rounded-lg bg-[rgba(55,224,200,0.14)] border border-[rgba(55,224,200,0.28)]">+ инфографика</span>
            <span className="text-[12px] px-[11px] py-1.5 rounded-lg bg-white/[0.06] border border-white/[0.12]">оптимизировать описание</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Trust bar ────────────────────────────────────────

const TRUST_STATS = [
  { value: SELLERS, label: "активных селлеров" },
  { value: "60 сек", label: "на анализ карточки" },
  { value: "4 в 1", label: "инструмента в кабинете" },
  { value: "API WB", label: "официальная интеграция" },
];

function TrustBar() {
  return (
    <section aria-label="Показатели" className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-5 pt-[22px] pb-2 border-t border-b border-white/[0.07]">
      {TRUST_STATS.map((s) => (
        <div key={s.label}>
          <div className="text-[30px] font-extrabold tracking-[-0.02em]">{s.value}</div>
          <div className="text-[13px] text-[#8b93ba] mt-0.5">{s.label}</div>
        </div>
      ))}
    </section>
  );
}

// ─── Features ─────────────────────────────────────────

function FeatureIcon({ variant }: { variant: "bars" | "layers" | "grid" | "bubble" }) {
  if (variant === "bars") {
    return (
      <div className="w-12 h-12 rounded-[13px] bg-[rgba(124,92,255,0.14)] border border-[rgba(124,92,255,0.28)] flex items-end justify-center gap-1 p-3 mb-5">
        <span className="w-[5px] h-2.5 bg-[var(--acc2)] rounded-[2px]" />
        <span className="w-[5px] h-[18px] bg-[var(--acc)] rounded-[2px]" />
        <span className="w-[5px] h-3.5 bg-[var(--acc2)] rounded-[2px]" />
      </div>
    );
  }
  if (variant === "layers") {
    return (
      <div className="w-12 h-12 rounded-[13px] bg-[rgba(55,224,200,0.12)] border border-[rgba(55,224,200,0.26)] relative mb-5">
        <span className="absolute left-3 top-3 w-[18px] h-[18px] rounded-[5px] bg-[var(--acc)]" />
        <span className="absolute left-5 top-5 w-[18px] h-[18px] rounded-[5px] bg-[var(--acc2)]" />
      </div>
    );
  }
  if (variant === "grid") {
    return (
      <div className="w-12 h-12 rounded-[13px] bg-[rgba(124,92,255,0.14)] border border-[rgba(124,92,255,0.28)] grid grid-cols-2 gap-1 p-3 mb-5">
        <span className="bg-[var(--acc)] rounded-[3px]" />
        <span className="bg-[var(--acc2)] rounded-[3px]" />
        <span className="bg-[var(--acc2)] rounded-[3px]" />
        <span className="bg-[var(--acc)] rounded-[3px]" />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-[13px] bg-[rgba(55,224,200,0.12)] border border-[rgba(55,224,200,0.26)] flex items-center justify-center mb-5">
      <span className="w-[22px] h-4 rounded-[8px_8px_8px_2px]" style={{ background: GRAD }} />
    </div>
  );
}

const FEATURES = [
  {
    variant: "bars" as const,
    title: "Анализ карточек и SEO",
    desc: "AI проверяет заголовок, описание, характеристики и подбирает ключевые запросы. Рекомендации по улучшению заголовка, SEO и визуала — за 60 секунд.",
  },
  {
    variant: "layers" as const,
    title: "Генерация студийных фото",
    desc: "Загрузите одно фото товара — нейросеть создаст студийные кадры, фоны и ракурсы. Без съёмки, фотографа и предметного стола.",
  },
  {
    variant: "grid" as const,
    title: "Продающая инфографика",
    desc: "Автоматическая инфографика с выгодами, характеристиками и УТП в едином стиле. Готовые макеты под размеры Wildberries.",
  },
  {
    variant: "bubble" as const,
    title: "Автоответы на отзывы",
    desc: "AI готовит варианты ответов на отзывы в вашем тон-оф-войс и помогает отрабатывать негатив. Вы проверяете и публикуете ответы самостоятельно — платформа не размещает их за вас.",
  },
];

function Features() {
  return (
    <section id="features" className="pt-[92px] pb-6">
      <h2 className="text-[clamp(24px,5.6vw,40px)] font-extrabold tracking-[-0.025em] m-0 mb-3.5 text-center">
        Четыре инструмента, один кабинет
      </h2>
      <p className="text-center text-[#aab2d6] text-[18px] m-0 mx-auto mb-[52px] max-w-[600px]">
        Всё, что нужно для сильной карточки и продаж — на базе искусственного интеллекта.
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[22px]">
        {FEATURES.map((f) => (
          <article key={f.title} className="border border-white/[0.09] rounded-[20px] p-8 bg-white/[0.02] transition-colors hover:border-white/[0.18]">
            <FeatureIcon variant={f.variant} />
            <h3 className="text-[22px] font-bold m-0 mb-2.5">{f.title}</h3>
            <p className="text-[#aab2d6] text-[15px] leading-[1.6] m-0">{f.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── How it works ─────────────────────────────────────

const STEPS = [
  {
    num: "01",
    title: "Подключите кабинет по API",
    desc: "Вставьте официальный API-ключ Wildberries. Логин и пароль не нужны — доступ безопасный.",
  },
  {
    num: "02",
    title: "Получите AI-анализ",
    desc: "Платформа оценит карточки, подсветит слабые места и предложит SEO, фото и инфографику.",
  },
  {
    num: "03",
    title: "Внедрите и анализируйте",
    desc: "Примените рекомендации, подготовьте ответы на отзывы и следите за динамикой показателей прямо в кабинете.",
  },
];

function HowItWorks() {
  return (
    <section id="how" className="pt-[92px] pb-6">
      <h2 className="text-[clamp(24px,5.6vw,40px)] font-extrabold tracking-[-0.025em] m-0 mb-[52px] text-center">
        Запуск за три шага
      </h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-[22px]">
        {STEPS.map((s) => (
          <div key={s.num} className="relative p-8 border border-white/[0.09] rounded-[20px]">
            <div className="font-mono text-[14px] text-[var(--acc2)] mb-4">{s.num}</div>
            <h3 className="text-[20px] font-bold m-0 mb-2.5">{s.title}</h3>
            <p className="text-[#aab2d6] text-[15px] leading-[1.6] m-0">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────

function Pricing() {
  return (
    <section id="pricing" className="pt-[92px] pb-6">
      <h2 className="text-[clamp(24px,5.6vw,40px)] font-extrabold tracking-[-0.025em] m-0 mb-3.5 text-center">
        Оплата по факту использования
      </h2>
      <p className="text-center text-[#aab2d6] text-[18px] m-0 mx-auto mb-11 max-w-[620px]">
        Без абонентской платы. Пополняете баланс и платите только за то, что реально использовали.
        Начать можно бесплатно.
      </p>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-[22px] items-stretch max-w-[900px] mx-auto">
        {/* Tokens plan (highlighted) */}
        <div className="flex flex-col border border-[var(--acc)] rounded-[22px] p-9 bg-[linear-gradient(180deg,rgba(124,92,255,0.12),rgba(255,255,255,0.02))] shadow-[0_20px_60px_rgba(124,92,255,0.18)]">
          <div className="font-mono text-[12px] text-[var(--acc2)] tracking-[0.05em] mb-3.5">AI-СЕРВИСЫ · ПО ТОКЕНАМ</div>
          <h3 className="text-[22px] font-bold m-0 mb-1.5">Анализ, фото, инфографика, тексты</h3>
          <p className="text-[#8b93ba] text-[14px] m-0 mb-[22px]">Списываем токены по мере работы нейросети</p>
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] text-[#8b93ba]">от</span>
            <span className="text-[44px] font-extrabold tracking-[-0.02em]">0,14</span>
            <span className="text-[16px] text-[#c3c9e6] font-semibold">коп. / токен</span>
          </div>
          <ul className="list-none p-0 mt-6 mb-0 flex flex-col gap-3 text-[#c3c9e6] text-[15px]">
            <li>Анализ карточек и SEO-рекомендации</li>
            <li>Генерация студийных фото</li>
            <li>Инфографика и продающие тексты</li>
            <li>Платите ровно за объём генерации</li>
          </ul>
          <a href={APP_URL} className="mt-7 text-center text-[#0a0f24] font-bold py-3.5 rounded-xl transition-transform hover:-translate-y-0.5" style={{ background: GRAD }}>
            Попробовать бесплатно
          </a>
        </div>

        {/* Auto-replies plan */}
        <div className="flex flex-col border border-white/[0.09] rounded-[22px] p-9 bg-white/[0.02]">
          <div className="font-mono text-[12px] text-[var(--acc2)] tracking-[0.05em] mb-3.5">АВТООТВЕТЫ · ПО ДЕЙСТВИЯМ</div>
          <h3 className="text-[22px] font-bold m-0 mb-1.5">Автоответы на отзывы</h3>
          <p className="text-[#8b93ba] text-[14px] m-0 mb-[22px]">Оплата за каждый сформированный ответ</p>
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] text-[#8b93ba]">от</span>
            <span className="text-[44px] font-extrabold tracking-[-0.02em]">0,30</span>
            <span className="text-[16px] text-[#c3c9e6] font-semibold">₽ / автоответ</span>
          </div>
          <ul className="list-none p-0 mt-6 mb-0 flex flex-col gap-3 text-[#c3c9e6] text-[15px]">
            <li>Ответы в вашем тон-оф-войс</li>
            <li>Обработка негатива</li>
            <li>Проверка перед публикацией</li>
            <li>Никакой ежемесячной платы</li>
          </ul>
          <a href={APP_URL} className={`${btnOutline} mt-7 py-3.5 font-bold`}>
            Попробовать бесплатно
          </a>
        </div>
      </div>

      <p className="text-center text-[#8b93ba] text-[13px] mt-[26px] mx-auto max-w-[640px] leading-[1.6]">
        Тарифицируемые единицы (токены, автоответы) и точные цены указаны в личном кабинете на{" "}
        <a href="https://app.mpprod.ru" className={legalLink}>app.mpprod.ru</a>.
        Оплата — предоплатой по <Legal href={OFFER_URL}>публичной оферте</Legal>{" "}
        (лицензионный договор). Сервис предназначен для предпринимателей (B2B).
      </p>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────

const FAQ_ITEMS: { q: string; a: ReactNode }[] = [
  {
    q: "Нужно ли передавать логин и пароль от кабинета WB?",
    a: <>Нет. Интеграция работает через официальный API-ключ Wildberries. Пароль от личного кабинета не запрашивается и не хранится.</>,
  },
  {
    q: "Как обрабатываются мои персональные данные?",
    a: (
      <>
        Данные обрабатываются на основании вашего согласия в соответствии с Федеральным законом
        №152-ФЗ «О персональных данных». Порядок описан в{" "}
        <Legal href={POLICY_URL}>Политике конфиденциальности</Legal>; данные размещаются на серверах в РФ.
      </>
    ),
  },
  {
    q: "Есть ли бесплатный доступ и как оплатить платные функции?",
    a: (
      <>
        Начать можно бесплатно. Платные функции предоставляются по{" "}
        <Legal href={OFFER_URL}>публичной оферте</Legal> — это лицензионный договор, а не
        потребительская подписка, на условиях предоплаты. Сервис для предпринимателей и самозанятых
        (B2B), поэтому Закон «О защите прав потребителей» к нему не применяется; порядок оплаты и
        возврата — в оферте.
      </>
    ),
  },
  {
    q: "Кому принадлежат сгенерированные фото и тексты?",
    a: (
      <>
        Права на использование результатов генерации переходят к вам на условиях{" "}
        <Legal href={OFFER_URL}>оферты</Legal>. Вы отвечаете за то, что исходные материалы (фото
        товара, бренд) не нарушают прав третьих лиц.
      </>
    ),
  },
];

function FAQ() {
  return (
    <section id="faq" className="pt-[92px] pb-6 max-w-[820px] mx-auto">
      <h2 className="text-[clamp(24px,5.6vw,40px)] font-extrabold tracking-[-0.025em] m-0 mb-11 text-center">
        Частые вопросы
      </h2>
      <div className="flex flex-col gap-3">
        {FAQ_ITEMS.map((item) => (
          <details key={item.q} className="border border-white/[0.09] rounded-2xl px-6 py-1 bg-white/[0.02]">
            <summary className="flex justify-between items-center py-5 text-[17px] font-semibold">
              {item.q}
              <span className="faq-plus text-[var(--acc2)] text-[24px] font-normal leading-none ml-4">+</span>
            </summary>
            <p className="text-[#aab2d6] text-[15px] leading-[1.6] m-0 mb-5">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────

function CTA() {
  return (
    <section id="cta" className="pt-[60px] pb-[92px]">
      <div className="border border-white/10 rounded-[28px] p-8 sm:p-14 text-center bg-[linear-gradient(135deg,rgba(124,92,255,0.16),rgba(55,224,200,0.08))]">
        <h2 className="text-[clamp(24px,5.6vw,38px)] font-extrabold tracking-[-0.025em] m-0 mb-3.5">
          Попробуйте MPPROD бесплатно
        </h2>
        <p className="text-[#c3c9e6] text-[18px] m-0 mx-auto mb-8 max-w-[520px]">
          Зарегистрируйтесь в кабинете и запустите анализ первой карточки — без оплаты на старте.
        </p>
        <div className="flex gap-3.5 justify-center flex-wrap">
          <a href={APP_URL} className="inline-block text-[#0a0f24] font-bold text-[17px] px-9 py-4 rounded-[14px] shadow-[0_12px_40px_rgba(124,92,255,0.35)] transition-transform hover:-translate-y-0.5" style={{ background: GRAD }}>
            Попробовать бесплатно
          </a>
          <a href={SUPPORT_TG} className={`${btnOutline} text-[17px] px-[30px] py-4`}>
            Задать вопрос в Telegram
          </a>
        </div>
        <p className="text-[#8b93ba] text-[13px] mt-[22px] mx-auto max-w-[520px]">
          Регистрируясь, вы принимаете условия <Legal href={OFFER_URL}>оферты</Legal> и соглашаетесь с{" "}
          <Legal href={POLICY_URL}>Политикой конфиденциальности</Legal>.
        </p>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────

const FOOTER_TOOLS = [
  { label: "Анализ карточек", href: "/instrumenty/analiz-kartochek/" },
  { label: "Генерация фото", href: "/instrumenty/generaciya-foto/" },
  { label: "Инфографика", href: "/instrumenty/infografika/" },
  { label: "Автоответы на отзывы", href: "/instrumenty/avtootvety-na-otzyvy/" },
  { label: "Все инструменты", href: "/instrumenty/" },
];

const FOOTER_COMPANY = [
  { label: "AI для Wildberries", href: "/wildberries/" },
  { label: "AI для Ozon", href: "/ozon/" },
  { label: "О сервисе", href: "/o-servise/" },
  { label: "Контакты", href: "/contacts/" },
];

function Footer() {
  const col = "text-[#aab2d6] hover:text-[#eef1fb] transition-colors";
  return (
    <footer className="relative z-[1] border-t border-white/[0.08] bg-[rgba(4,7,22,0.6)]">
      <div className="max-w-[1200px] mx-auto px-6 pt-14 pb-10 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-9">
        <div>
          <div className="flex items-center gap-2.5 font-extrabold text-[19px] mb-4">
            <span className="inline-flex w-7 h-7 rounded-lg items-center justify-center font-mono text-[14px] text-[#0a0f24]" style={{ background: GRAD }}>M</span>
            MPPROD
          </div>
          <p className="text-[#8b93ba] text-[14px] leading-[1.65] m-0 mb-[18px] max-w-[340px]">
            AI-платформа для эффективной работы с карточками на маркетплейсах. Анализ карточек,
            генерация фото и инфографики, автоответы на отзывы.
          </p>
          <div className="text-[13px] text-[#6b74a0] leading-[1.7]">
            <div className="text-[#8b93ba] font-semibold mb-1">Поддержка</div>
            <div><a href={SUPPORT_TG} className={legalLink}>Telegram @mpprod_support</a></div>
          </div>
        </div>

        <div>
          <div className="font-bold text-[15px] mb-4">Инструменты</div>
          <div className="flex flex-col gap-[11px] text-[14px]">
            {FOOTER_TOOLS.map((l) => (
              <a key={l.href} href={l.href} className={col}>{l.label}</a>
            ))}
          </div>
        </div>

        <div>
          <div className="font-bold text-[15px] mb-4">Компания</div>
          <div className="flex flex-col gap-[11px] text-[14px]">
            {FOOTER_COMPANY.map((l) => (
              <a key={l.href} href={l.href} className={col}>{l.label}</a>
            ))}
          </div>
        </div>

        <div>
          <div className="font-bold text-[15px] mb-4">Документы и право</div>
          <div className="flex flex-col gap-[11px] text-[14px]">
            <a href={POLICY_URL} target="_blank" rel="noopener" className={col}>Политика конфиденциальности</a>
            <a href={OFFER_URL} target="_blank" rel="noopener" className={col}>Оферта и соглашение</a>
            <a href="/soglasie-na-obrabotku-pd/" className={col}>Согласие на обработку ПД</a>
            <a href="/cookie/" className={col}>Использование cookie</a>
            <a href="/pravila-ai/" className={col}>Правила использования AI</a>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 pb-8">
        <p className="text-[12px] text-[#6b74a0] leading-[1.65] m-0 mb-5 border-t border-white/[0.06] pt-6">
          Рекомендации, аналитика и материалы, сгенерированные платформой, носят информационный
          характер. MPPROD не гарантирует роста продаж, позиций в выдаче или прохождения модерации
          маркетплейса — решения Пользователь принимает самостоятельно. MPPROD не является частью
          Wildberries и не аффилирован с маркетплейсами; названия товарных площадок используются
          исключительно для указания совместимости. Интеграция — через официальный API.
        </p>
        <div className="flex justify-between flex-wrap gap-3 text-[13px] text-[#6b74a0]">
          <span>© 2026 MPPROD. Все права защищены.</span>
          <span>Сервис для предпринимателей и самозанятых (B2B)</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Cookie consent (152-ФЗ) ──────────────────────────

function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem("mpprod_cookie_v1")) setShow(true);
    } catch {
      setShow(true);
    }
  }, []);

  const store = (val: string) => {
    try {
      localStorage.setItem("mpprod_cookie_v1", val);
    } catch {
      /* storage unavailable — dismiss anyway */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[100] p-4 flex justify-center">
      <div className="max-w-[920px] w-full bg-[rgba(13,18,42,0.96)] backdrop-blur-[14px] border border-white/[0.12] rounded-2xl px-[22px] py-[18px] flex flex-col sm:flex-row flex-wrap sm:items-center gap-4 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <p className="m-0 flex-1 min-w-[200px] text-[14px] leading-[1.55] text-[#c3c9e6]">
          Мы используем файлы cookie и метрики для работы сайта и аналитики. Продолжая пользоваться
          сайтом, вы соглашаетесь с обработкой cookie в соответствии с{" "}
          <Legal href={POLICY_URL}>Политикой конфиденциальности</Legal>.
        </p>
        <div className="flex gap-2.5">
          <button
            onClick={() => store("necessary")}
            className="flex-1 min-h-11 border border-white/[0.18] bg-transparent text-[#c3c9e6] font-semibold text-[14px] px-[18px] py-[11px] rounded-[11px] cursor-pointer transition-colors hover:text-[#eef1fb]"
          >
            Только необходимые
          </button>
          <button
            onClick={() => store("all")}
            className="flex-1 min-h-11 border-none text-[#0a0f24] font-bold text-[14px] px-5 py-[11px] rounded-[11px] cursor-pointer transition-transform hover:-translate-y-0.5"
            style={{ background: GRAD }}
          >
            Принять все
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────

export function App() {
  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Ambient glows */}
      <div
        className="absolute -top-[260px] left-1/2 -translate-x-1/2 w-[900px] h-[900px] blur-[20px] pointer-events-none z-0"
        style={{ background: "radial-gradient(circle,rgba(124,92,255,0.28),transparent 62%)" }}
      />
      <div
        className="absolute top-[520px] -right-[200px] w-[640px] h-[640px] blur-[20px] pointer-events-none z-0"
        style={{ background: "radial-gradient(circle,rgba(55,224,200,0.16),transparent 65%)" }}
      />

      <Header />

      <main id="top" className="relative z-[1] max-w-[1200px] mx-auto px-[18px] min-[861px]:px-6">
        <Hero />
        <TrustBar />
        <Features />
        <HowItWorks />
        <Pricing />
        <FAQ />
        <CTA />
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
}

export default App;
